import {
  AfterViewChecked,
  Component,
  ElementRef,
  Input,
  OnChanges,
  ViewChild,
} from '@angular/core';
import { AprimoVideo } from '../../utils/aprimo-video.util';

interface SubtitleTrackView {
  language: string;
  url: string;
  label: string;
  isDefault: boolean;
}

/**
 * Plays a self-hosted Aprimo video with a caption track per available locale.
 *
 * `<source>`, `<track>` and `poster` bind in Angular's URL security context, so https URLs are
 * sanitized by the framework and no DomSanitizer bypass is required here. `crossorigin` is
 * mandatory: without it the browser refuses to load the cross-origin WebVTT tracks.
 */
@Component({
  selector: 'mpp-aprimo-video',
  templateUrl: './aprimo-video.component.html',
  styleUrls: ['./aprimo-video.component.scss'],
  standalone: false,
})
export class AprimoVideoComponent implements OnChanges, AfterViewChecked {
  @Input() video: AprimoVideo;
  @Input() poster: string;
  @Input() preferredLanguage: string;
  @Input() autoplay = false;
  /**
   * Defaults to 'none' so a player sitting in a hidden overlay costs nothing on page load; the
   * poster still renders. Callers where the video is the primary content can request 'metadata'.
   */
  @Input() preload: 'none' | 'metadata' | 'auto' = 'none';

  @ViewChild('videoElement') videoElement: ElementRef<HTMLVideoElement>;

  tracks: SubtitleTrackView[] = [];
  autoplayEnabled = false;

  private loadedVideoUrl: string;
  private reloadPending = false;
  private resumeAfterReload = false;

  play(): void {
    // play() rejects when the browser blocks unmuted autoplay; the user can still hit the control.
    this.videoElement?.nativeElement?.play()?.catch(() => undefined);
  }

  pause(): void {
    const element = this.videoElement?.nativeElement;
    if (element) {
      element.pause();
      element.currentTime = 0;
    }
  }

  ngOnChanges(): void {
    // A media element only reads its <source> children during resource selection, which happens
    // once. Rebinding the src after a language change is ignored until load() is called, so the
    // previous locale's video would keep playing.
    if (this.video?.videoUrl !== this.loadedVideoUrl) {
      // Only force a reload when swapping a source the element has already selected. On the first
      // binding the element runs resource selection on its own, and load() would abort that
      // in-flight fetch just to start an identical one.
      const isFirstBinding = this.loadedVideoUrl === undefined;
      this.loadedVideoUrl = this.video?.videoUrl;
      this.reloadPending = !isFirstBinding;
      // Autoplay is a first-impression behaviour, not a property of the source. Leaving the
      // attribute on would restart a video the viewer had deliberately paused every time they
      // switched language, so it is spent once the element has selected its first source.
      if (isFirstBinding) {
        this.autoplayEnabled = this.autoplay;
      } else {
        this.autoplayEnabled = false;
        this.resumeAfterReload = this.videoElement?.nativeElement?.paused === false;
      }
    }

    const subtitles = this.video?.subtitles || [];
    const preferredIndex = this.findPreferredTrackIndex(subtitles);
    this.tracks = subtitles.map((subtitle, index) => ({
      language: subtitle.language,
      url: subtitle.url,
      label: this.getLanguageLabel(subtitle.language),
      isDefault: index === preferredIndex,
    }));
  }

  // Deferred to after view checks so the <source> and <track> bindings are in the DOM before the
  // element re-reads them.
  ngAfterViewChecked(): void {
    const element = this.videoElement?.nativeElement;
    if (!this.reloadPending || !element) {
      return;
    }
    this.reloadPending = false;
    element.load();
    // load() always resets the element to paused, so a viewer who was mid-playback when they
    // switched language has to be put back into it explicitly.
    if (this.resumeAfterReload) {
      this.resumeAfterReload = false;
      this.play();
    }
  }

  // Only one <track> may carry `default`, so pick a single winner: an exact match first, then the
  // first track sharing the primary subtag, so "en" still selects an "en-us" track.
  private findPreferredTrackIndex(subtitles: { language: string }[]): number {
    const preferred = (this.preferredLanguage || '').toLowerCase();
    if (!preferred) {
      return -1;
    }
    const exact = subtitles.findIndex((s) => s.language.toLowerCase() === preferred);
    if (exact !== -1) {
      return exact;
    }
    return subtitles.findIndex(
      (s) => s.language.toLowerCase().split('-')[0] === preferred.split('-')[0],
    );
  }

  private getLanguageLabel(language: string): string {
    try {
      const displayNames = new Intl.DisplayNames([language], { type: 'language' });
      return displayNames.of(language) || language.toUpperCase();
    } catch {
      return language.toUpperCase();
    }
  }
}
